import { Navigate, type RouteObject } from 'react-router'

import { PageWaveLoader } from '~/shared/components/PageWaveLoader'
import { Bootstrap } from '~/shell/app/Bootstrap'
import { CareGapsLayout } from '~/shell/app/CareGapsLayout'
import { LegacyCareGapsRedirect } from '~/shell/app/LegacyCareGapsRedirect'
import { getEnabledApps, mountedAppRoutes } from '~/shell/app/registry'
import { RequireAuth } from '~/shell/auth/RequireAuth'
import { UnauthorizedPage } from '~/shell/pages/unauthorized/UnauthorizedPage'

/**
 * The shell's route configuration: the auth guard, the customer segment, and every registered app
 * mounted beneath them at its own literal segment.
 *
 * `RequireAuth` is the outermost layout/`Outlet` parent: unauthenticated users are
 * redirected to Auth0 login before any child route renders (AUTH-04).
 * `CareGapsLayout` sits inside the guard so the app header renders only for
 * authenticated sessions, and above the routed page so the chrome is not
 * re-declared per route (SHELL-01). It also owns the `c/:customer` segment, which
 * every query beneath it is scoped by (SHELL-14).
 *
 * The app-less index redirects to the first enabled app rather than rendering the header over an
 * empty outlet (SHELL-17). "First enabled" is arc's deliberate placeholder for the entitlement
 * fallback chain; with one app there is nothing to choose between, and the seam is already the one
 * arc's is.
 *
 * The root index runs `Bootstrap`, which resolves which customer the session lands on from
 * what it is entitled to — its claim, or its accessible list narrowed by what it viewed
 * last (SHELL-15). `unauthorized` is the page it sends a session with no customer at all
 * to, and it belongs to the shell rather than an app for the same reason: such a session
 * has no app to render it.
 *
 * Each app mounts behind a `lazy` route, so its pages are a chunk of their own fetched on the first
 * navigation into it (Atlas ADR-0019). A cold deep link into an app therefore waits on that chunk
 * before anything renders, which is what the root's `HydrateFallback` covers — without it the router
 * paints nothing until the chunk lands.
 *
 * `care-gaps/*` beneath the customer is the URL shape Scout served before the app segment existed,
 * kept as a redirect so an existing bookmark survives (see `LegacyCareGapsRedirect`).
 *
 * Nothing here names an app: the router is assembled from `registry.ts`, which is the one shell
 * module that does. That is the seam arc's registry holds, and this file becomes arc's `routes.tsx`
 * when Scout moves into the suite.
 *
 * Exported for use in tests with `createMemoryRouter`.
 */
export const routes: RouteObject[] = [
  {
    children: [
      {
        children: [
          ...mountedAppRoutes(),
          {
            element: <LegacyCareGapsRedirect />,
            path: 'care-gaps/*',
          },
          {
            element: <Navigate replace to={getEnabledApps()[0].id} />,
            index: true,
          },
        ],
        element: <CareGapsLayout />,
        path: 'c/:customer',
      },
      {
        element: <Bootstrap />,
        index: true,
      },
      {
        element: <UnauthorizedPage />,
        path: 'unauthorized',
      },
    ],
    element: <RequireAuth />,
    HydrateFallback: PageWaveLoader,
    path: '/',
  },
]
